/* eslint-disable prefer-destructuring */
/**
 * Core Language & Domain Utilities
 * ================================
 * Single source of truth for all language/domain detection and manipulation.
 *
 * This module consolidates commonly used language utilities to:
 * 1. Eliminate duplicate code across the codebase
 * 2. Provide a single import point for all language-related functions
 * 3. Handle China domain detection and cross-domain navigation
 * 4. Manage URL path language prefixes
 * 5. Handle Language Persistence (Cookies)
 * 6. Sync Language from URL (Redux)
 * 7. Transform Routes
 */

import {
  languagesForAmberUserWebsite,
  LanguageConfig,
  SUPPORTED_LANG_VALUES,
  LANGUAGE_URL_PREFIXES,
  CHINA_HEADER_LANGUAGE_CODES,
  PICKER_LANGUAGES,
} from "locales/langConfig/langConfig";
import setCookie from "helpers/setCookie";
import { getCookie } from "helpers/getCookie";
import config from "@Config/index";
import { appShellFromRequest, isNewNativeAppShell } from "constants/appShell";

// =============================================================================
// Constants & Types
// =============================================================================

export const SUPPORTED_LANGUAGES = SUPPORTED_LANG_VALUES;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Language-prefixed routes that render `noindex,nofollow`.
 *
 * Two groups, deliberately on different footings:
 *   - fr / es / de — noindex, but still sitemapped (`s.base` generates `/fr|/es|/de/sitemap.xml`
 *     and `s.web`'s robots.ejs declares them) and still hreflang-annotated. Existing behaviour.
 *   - vi / th / tr — noindex AND no sitemap AND no robots.txt entry AND no hreflang AND no
 *     crawlable SSR link. See SSR_HIDDEN_LANGS below, HREFLANG_EXCLUDED_LANGS in
 *     generateHreflangUrls.ts, and the block comment in langConfig.ts.
 *
 * NOTE: this list does NOT control hreflang any more — generateHreflangUrls.ts stopped
 * consulting it. Excluding a locale from hreflang requires HREFLANG_EXCLUDED_LANGS.
 */
export const NOINDEX_LANG_PREFIXES = ["fr", "es", "de", "vi", "th", "tr", "zhtw", "zhhk"] as const;

export const isNoindexLang = (lang?: string): boolean =>
  !!lang && (NOINDEX_LANG_PREFIXES as readonly string[]).includes(lang);

/**
 * Locales withheld from the server-rendered language links in `LanguageSelectorModalSSR`,
 * so crawlers are never handed a URL for them.
 *
 * Kept SEPARATE from NOINDEX_LANG_PREFIXES on purpose: fr/es/de have had crawlable SSR
 * language links since launch and are sitemapped, so removing theirs would be a live change to
 * their crawlability. This list hides the new locales without touching them. Do not merge them.
 */
export const SSR_HIDDEN_LANGS = ["vi", "th", "tr", "zhtw", "zhhk"] as const;

export const isSsrHiddenLang = (lang?: string): boolean =>
  !!lang && (SSR_HIDDEN_LANGS as readonly string[]).includes(lang);

/** Language route prefix pattern for react-router ":lang(zhtw|fr|...)" */
const ROUTE_LANG_PATTERN = LANGUAGE_URL_PREFIXES.join("|");
export const LANGUAGE_PREFIX_PATTERN = `:lang(${ROUTE_LANG_PATTERN})`;

const LANGUAGE_PREFIX_REGEX = new RegExp(`^\\/(${ROUTE_LANG_PATTERN})\\/`);
const LANGUAGE_ONLY_REGEX = new RegExp(`^\\/(${ROUTE_LANG_PATTERN})$`);
const LANGUAGE_COOKIE_KEY = "user_language";

// =============================================================================
// Domain Detection
// =============================================================================

/**
 * Check if a hostname is a China domain (.cn)
 */
export const isChinaDomain = (hostname: string): boolean =>
  hostname.endsWith("amberstudent.cn") ||
  hostname === "amberstudent.cn" ||
  hostname.endsWith("-china.staging-amber.com");

/**
 * Client-side check for China domain (safe for SSR)
 */
export const isChinaDomainClient = (): boolean => {
  if (typeof window === "undefined") return false;
  return isChinaDomain(window.location.hostname);
};

// =============================================================================
// Language Configuration Lookups
// =============================================================================

/** Any locale, including one another build serves (zhtw on `.com`, vi on whitelabel). */
export const getLanguageConfig = (languageCode: string): LanguageConfig | undefined =>
  PICKER_LANGUAGES.find((lang) => lang.code === languageCode);

export const getDomainForLanguage = (languageCode: string): string | null => {
  const language = getLanguageConfig(languageCode);
  if (language && language.domains.length > 0) {
    return `https://${language.domains[0]}`;
  }
  return null;
};

export const getLanguageFromHostname = (hostname: string): string => {
  // China build on localhost / non-.cn hosts (dev, PR env) — default Simplified Chinese, not English.
  if (config.IS_CHINA_APP || isChinaDomain(hostname)) return "zh";
  const matchedLanguage = languagesForAmberUserWebsite.find((lang) =>
    lang.domains.includes(hostname),
  );
  return matchedLanguage?.code || "en";
};

export const getLanguageFromRequest = (req: any): string => {
  const pathname = req?.path || req?.url || "";
  const fromPath = extractLanguageFromPath(pathname);
  if (fromPath) return fromPath;

  const hostname = req?.hostname || req?.headers?.host || "";
  return getLanguageFromHostname(hostname);
};

export const isChinaSameDomainLanguage = (code: string): boolean =>
  (CHINA_HEADER_LANGUAGE_CODES as readonly string[]).includes(code);

/**
 * Site-wide header language control on `.cn`. Mini-program and native-app WebView stay as on
 * release: no picker (FR-13 / FR-0). Browser `.cn` shows the full list (`getPickerLanguages`).
 */
export const shouldShowSiteLanguageSelector = (
  showLangSelector: boolean,
  opts: { isMiniProgram?: boolean; isApp?: boolean },
): boolean => {
  if (!showLangSelector) return false;
  if (!config.IS_CHINA_APP) return true;
  return !(opts.isMiniProgram || opts.isApp);
};

// =============================================================================
// Path Manipulation (from languageRouting)
// =============================================================================

export const isSupportedLanguage = (lang: string): lang is SupportedLanguage =>
  SUPPORTED_LANG_VALUES.includes(lang);

export const extractLanguageFromPath = (path: string): SupportedLanguage | null => {
  const match = path.match(LANGUAGE_PREFIX_REGEX) || path.match(LANGUAGE_ONLY_REGEX);
  return match ? (match[1] as SupportedLanguage) : null;
};

export const hasLanguagePrefix = (path: string): boolean =>
  LANGUAGE_PREFIX_REGEX.test(path) || LANGUAGE_ONLY_REGEX.test(path);

export const addLanguageToPath = (
  path: string,
  language: string,
  donotAddPrefixSlash = false,
): string => {
  if (language === "en" || language === "zh") {
    return removeLanguageFromPath(path);
  }

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const normalizedPath = removeLanguageFromPath(path);
  const withSlash = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  return `${donotAddPrefixSlash ? "" : "/"}${language}${withSlash}`;
};

export const removeLanguageFromPath = (path: string): string =>
  path.replace(LANGUAGE_PREFIX_REGEX, "/").replace(LANGUAGE_ONLY_REGEX, "/");

/**
 * Convert a route path to support both with and without language prefix.
 * When a new language is added to langConfig, all routes automatically pick it up.
 *
 * @example
 *   withLang("/places/:id") → ["/places/:id", "/:lang(en|zh|fr|es|de|zhtw|zhhk)/places/:id"]
 *   withLang("/places/:id/video/:videoId", "/places/:id/video")
 *     → ["/places/:id/video/:videoId", "/places/:id/video",
 *        "/:lang(...)/places/:id/video/:videoId", "/:lang(...)/places/:id/video"]
 */
/**
 * Paths that exist in English only, so a link to them must never gain a `/fr|es|de/` prefix —
 * `/fr/about` and friends are 404s on staging and production.
 *
 *   · `/about` and `/connect` are served by other apps (nginx hands them to s.web and the
 *     partner dashboard), which have no language routes.
 *   · `/job-opening` has no `withLang()` route in this app.
 *
 * Give a page a `withLang()` route and it can come off this list.
 */
export const ENGLISH_ONLY_PATHS = ["/about", "/connect", "/job-opening"];

/** True when `path` is one of `ENGLISH_ONLY_PATHS` (or below it), ignoring any language prefix. */
export const isEnglishOnlyPath = (path: string): boolean => {
  const bare = removeLanguageFromPath(path).split(/[?#]/)[0];
  return ENGLISH_ONLY_PATHS.some((p) => bare === p || bare.startsWith(`${p}/`));
};

export const withLang = (...paths: string[]): string[] => {
  const result: string[] = [];
  for (const p of paths) {
    if (p.includes(":lang")) {
      result.push(p);
    } else {
      const normalized = p.startsWith("/") ? p : `/${p}`;
      result.push(normalized);
    }
  }
  for (const p of paths) {
    if (!p.includes(":lang")) {
      const normalized = p.startsWith("/") ? p : `/${p}`;
      result.push(`/${LANGUAGE_PREFIX_PATTERN}${normalized}`);
    }
  }
  return result;
};

/** @deprecated Use `withLang` instead */
export const makeLanguageRoutes = (path: string): string[] => withLang(path);

// =============================================================================
// URL Building (from languageUrlBuilder)
// =============================================================================

/**
 * True when a switch crosses between the Chinese locales (served from `.cn`) and the rest (served
 * from `.com`). Only then does the URL move to the target's home domain; a switch within one side
 * stays on the current host, so localhost, PR envs and whitelabel partner hosts keep working.
 */
export const needsCrossDomainNavigation = (
  currentLanguage: string,
  targetLanguage: string,
): boolean =>
  isChinaSameDomainLanguage(currentLanguage) !== isChinaSameDomainLanguage(targetLanguage);

interface BuildLanguageUrlOptions {
  path: string;
  targetLanguage: string;
  currentLanguage?: string;
  preserveSearch?: boolean;
  isExternal?: boolean;
}

interface LanguageUrlResult {
  url: string;
  needsCrossDomainNavigation: boolean;
  targetDomain: string | null;
}

export const buildLanguageUrl = (options: BuildLanguageUrlOptions): LanguageUrlResult => {
  const {
    path,
    targetLanguage,
    currentLanguage = "en",
    preserveSearch = true,
    isExternal = false,
  } = options;

  if (isExternal || path.startsWith("http://") || path.startsWith("https://")) {
    return { url: path, needsCrossDomainNavigation: false, targetDomain: null };
  }

  const localizedPath = addLanguageToPath(path.startsWith("/") ? path : `/${path}`, targetLanguage);
  const searchParams =
    preserveSearch && typeof window !== "undefined" ? window.location.search : "";
  const targetDomain = needsCrossDomainNavigation(currentLanguage, targetLanguage)
    ? getDomainForLanguage(targetLanguage)
    : null;

  return {
    url: `${targetDomain ?? ""}${localizedPath}${searchParams}`,
    needsCrossDomainNavigation: !!targetDomain,
    targetDomain,
  };
};

export const buildCurrentLanguageUrl = (
  path: string,
  currentLanguage: string,
  isExternal = false,
): string =>
  buildLanguageUrl({
    path,
    targetLanguage: currentLanguage,
    currentLanguage,
    isExternal,
  }).url;

export const shouldAddLanguagePrefix = (language: string): boolean =>
  language !== "zh" && language !== "en";

/** On `.com` these wait for the SHOW_NEW_LOCALES flag; `.cn` lists them always (no GrowthBook). */
const FLAG_GATED_LANGS = ["vi", "th", "tr"];

export interface PickerLanguage {
  code: string;
  label: string;
  flag?: any;
}

/**
 * The "Select Preferences" language list, in display order (P1D-5521 follow-up).
 *
 * Browser `.com` and `.cn` list every locale in fixed `PICKER_LANGUAGES` order; a pick across the
 * Chinese / non-Chinese line switches domain (`buildLanguageUrl`). The mini-program (zh only), the
 * global app WebView and whitelabel keep their earlier lists. Every option shows its flag on both
 * sites; the three Chinese locales share the China flag (`langConfig`).
 */
export const getPickerLanguages = ({
  currentLanguage,
  showNewLocales,
  isMiniProgram,
  isApp,
}: {
  currentLanguage: string;
  showNewLocales: boolean;
  isMiniProgram: boolean;
  isApp: boolean;
}): PickerLanguage[] => {
  // Keep the user's current locale listed so someone already on /vi can see it and switch away.
  const hidden =
    config.IS_CHINA_APP || showNewLocales
      ? []
      : FLAG_GATED_LANGS.filter((code) => code !== currentLanguage);

  let languages: LanguageConfig[];
  if (config.IS_CHINA_APP && isMiniProgram) {
    languages = PICKER_LANGUAGES.filter((lang) => lang.code === "zh");
  } else if (!config.IS_CHINA_APP && (isApp || config.IS_WHITELABEL_APP)) {
    // Unchanged: this build's locales without zh, current language first.
    const own = languagesForAmberUserWebsite.filter(
      (lang) => lang.code !== "zh" && !hidden.includes(lang.code),
    );
    languages = [
      ...own.filter((lang) => lang.code === currentLanguage),
      ...own.filter((lang) => lang.code !== currentLanguage),
    ];
  } else {
    languages = PICKER_LANGUAGES.filter((lang) => !hidden.includes(lang.code));
  }

  return languages.map((lang) => ({
    code: lang.code,
    label: lang.nativeLabel,
    flag: lang.flag,
  }));
};

// =============================================================================
// Cross-Domain Switching (from customDomainSwitch)
// =============================================================================

export const customDomainSwitch = ({ url }: { url: string }): void => {
  if (!url) return;
  window.location.href = url;
};

// =============================================================================
// Persistence (Merged from languagePersistence.ts)
// =============================================================================

/**
 * Utility for persisting user language preference
 *
 * NOTE: We use Cookies ONLY (not LocalStorage) because the server (SSR)
 * needs to read this preference to render the correct language.
 */

export const saveLanguagePreference = (language: string): void => {
  if (typeof window === "undefined") return;

  // Determine the correct domain
  let domain = "";
  const hostname = window.location.hostname;

  if (hostname.includes("amberstudent.cn")) {
    // China domain
    domain = ".amberstudent.cn";
  } else if (hostname.includes("amberstudent.com")) {
    // Main production domain
    domain = ".amberstudent.com";
  } else if (hostname.includes("staging-amber.com")) {
    // Staging domain
    domain = ".staging-amber.com";
  }
  // For localhost and other domains, leave domain empty (defaults to current host)

  setCookie({
    cname: LANGUAGE_COOKIE_KEY,
    cvalue: language,
    exdays: 365,
    secure: !__DEV__,
    domain,
  });

  // Debug log to verify cookie is saved (only in development)
  if (__DEV__) {
    console.log(`[Language] Saved language preference: ${language}`);
    console.log(`[Language] Cookie domain: ${domain || "current host"}`);
    console.log(`[Language] Cookie value:`, getCookie(LANGUAGE_COOKIE_KEY));
  }
};

export const getLanguagePreference = (): string | null => {
  const cookieVal = getCookie(LANGUAGE_COOKIE_KEY);
  return cookieVal || null;
};

export const getLanguageFromCookie = (): string | null => {
  const cookieVal = getCookie(LANGUAGE_COOKIE_KEY);
  return cookieVal || null;
};

export const clearLanguagePreference = (): void => {
  if (typeof window === "undefined") return;

  // Determine the correct domain
  let domain = "";
  const hostname = window.location.hostname;

  if (hostname.includes("amberstudent.cn")) {
    domain = ".amberstudent.cn";
  } else if (hostname.includes("amberstudent.com")) {
    domain = ".amberstudent.com";
  } else if (hostname.includes("staging-amber.com")) {
    domain = ".staging-amber.com";
  }

  setCookie({
    cname: LANGUAGE_COOKIE_KEY,
    cvalue: "",
    exdays: -1,
    secure: !__DEV__,
    domain,
  });
};

export const hasLanguagePreference = (): boolean => getLanguagePreference() !== null;

// =============================================================================
// Sync Logic (Moved to languageReduxHelpers.ts to avoid circular dependency)
// =============================================================================

/**
 * NOTE: Redux-dependent sync functions have been moved to:
 * src/utils/languageReduxHelpers.ts
 *
 * This was necessary to break the circular dependency:
 * store/index.ts → languageRouterMiddleware → languageCore → store/index.ts
 *
 * Functions moved:
 * - syncLanguageFromUrl
 * - syncLanguageFromUrlClient
 * - languageFromUrlMiddleware
 */

/**
 * Get user language for SSR (Server-Side Rendering)
 * Used by China domain to determine initial language
 *
 * Priority:
 * 1. Language from URL path (e.g., /fr/search)
 * 2. Language from query param (e.g., ?lang=zh)
 * 3. Language from domain (e.g., amberstudent.cn = zh)
 */
export const getUserLanguageSSR = (req: any): string => {
  const pathname = req.path || req.url || "";
  const hostDomain = req.hostname || req.host?.split(":")[0] || "";
  const requestedLang = extractLanguageFromPath(pathname) || req.query?.lang;

  if (requestedLang && SUPPORTED_LANGUAGES.includes(requestedLang)) {
    return requestedLang;
  }

  if (config.IS_CHINA_APP || isChinaDomain(hostDomain)) {
    return "zh";
  }

  const englishDomains = languagesForAmberUserWebsite.find(
    (lang) => lang.name === "english",
  )?.domains;

  if (englishDomains?.includes(hostDomain)) {
    return "en";
  }

  return "en";
};

// =============================================================================
// Route Transformation (Merged from transformRoutesWithLanguage.ts)
// =============================================================================

/**
 * Transform a single route to support language prefixes
 * Creates duplicate routes - one with language prefix and one without
 */
const transformRoute = (route: any): any[] => {
  // If no path or it's a redirect, return as is
  if (!route.path || route.redirect) {
    return [route];
  }

  // If path already has language pattern, return as is
  if (route.path.includes(":lang")) {
    return [route];
  }

  const basePath = route.path;
  const languagePrefixedPath = `/${LANGUAGE_PREFIX_PATTERN}${basePath}`;

  // Return two routes: one with language prefix (higher priority) and one without
  return [
    {
      ...route,
      path: languagePrefixedPath,
    },
    route, // Original route without language prefix
  ];
};

/**
 * Transform route configuration to support language prefixes
 * Recursively processes nested routes
 */
export const transformRoutesWithLanguage = (routes: any[]): any[] => {
  const transformedRoutes: any[] = [];

  routes.forEach((route) => {
    // Transform nested routes if they exist
    if (route.routes && Array.isArray(route.routes)) {
      const expandedRoutes: any[] = [];

      route.routes.forEach((nestedRoute: any) => {
        const transformed = transformRoute(nestedRoute);
        expandedRoutes.push(...transformed);
      });

      transformedRoutes.push({
        ...route,
        routes: expandedRoutes,
      });
    } else {
      // Transform single route
      const transformed = transformRoute(route);
      transformedRoutes.push(...transformed);
    }
  });

  return transformedRoutes;
};

export default transformRoutesWithLanguage;

/**
 * The user's currency for a server-rendered request.
 *
 * `app-currency` first, cookie second — but **only from a known native shell**. The app writes
 * the cookie into `WKHTTPCookieStore` and navigates in the same breath, and that write is not
 * reliably visible to the request that follows, so the first load after a currency change could
 * render in the previous currency while a refresh of the same page rendered correctly. A header
 * is part of the request and cannot lose that race.
 *
 * Gated on the shell so an arbitrary client cannot override a user's currency by sending a
 * header; everywhere else this is cookie-only, exactly as before.
 */
export const resolveUserCurrency = (req: any): string => {
  // Never resolve a currency when running as the China app inside a native shell.
  if (config.IS_CHINA_APP && isNewNativeAppShell(appShellFromRequest(req ?? {}))) {
    return "";
  }
  if (isNewNativeAppShell(appShellFromRequest(req ?? {}))) {
    const fromApp = req?.headers?.["app-currency"];
    if (typeof fromApp === "string" && fromApp) return fromApp;
  }
  return req?.cookies?.user_currency || "";
};

/**
 * The user's language for a server-rendered request.
 *
 * Same reasoning as ``resolveUserCurrency``, and the same bug it was written for: the app seeds
 * `user_language` into `WKHTTPCookieStore` and navigates in the same breath, so the cookie is not
 * reliably visible to the request that follows. Reading the cookie alone meant the first page
 * opened after a language change still rendered in the previous language — and often the second —
 * until the write landed. The app already sends `app-language` on every request it issues
 * (`WebContainerView.applyAppHeaders`) carrying the same value; a header is part of the request
 * and cannot lose that race.
 *
 * Gated on the shell so an arbitrary client cannot force a language via a header. The cookie is
 * still the fallback, and still what SPA navigations the page makes on its own rely on.
 */
export const resolveUserLanguage = (req: any): string => {
  if (isNewNativeAppShell(appShellFromRequest(req ?? {}))) {
    const fromApp = req?.headers?.["app-language"];
    if (typeof fromApp === "string" && fromApp) return fromApp;
  }
  return req?.cookies?.user_language || "";
};
